import { useCallback, useEffect, useId, useState } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  ItemPhysicalProfile,
  SetPhysicalFactsRequest,
} from "@shared-core/data/api-types";
import {
  extractFieldErrors,
  type FieldError,
} from "@shared-core/data/validation";
import { formatDateOrDash } from "@shared-core/domain/formatters";
import { mutationErrorMessage } from "@shared-core/domain/mutation-error-message";
import { Badge } from "../../primitives/badge";
import { Button } from "../../primitives/button";
import { Input } from "../../primitives/input";
import { Label } from "../../primitives/label";

/**
 * The "Weight and packing" section of a saved catalogue item in the editor
 * (physical-properties 06). Shows where the item's weight and packed carton
 * come from (derived from the product, the supplier's figures, entered by a
 * person, or not set yet) and the figures. Only where nothing derived or
 * supplied gives them (source `missing` or `entered`) does it offer the entry
 * form; the write path is the read's `set_physical_facts_url` /
 * `clear_physical_facts_url`, never built here, and a null URL is a step this
 * user may not take. Refusals are field-tagged and sit next to their inputs.
 *
 * Saved at once (not part of the catalogue draft the page header saves).
 */

const SOURCE_LABEL: Record<
  ItemPhysicalProfile["physical_profile_source"],
  string
> = {
  derived: "Worked out from the product",
  supplier: "Supplier's figures",
  entered: "Entered by hand",
  missing: "Not set",
};

type FormField = keyof SetPhysicalFactsRequest;

type FormState = Record<FormField, string>;

const FIELDS: { key: FormField; label: string; unit: string; hint?: string }[] =
  [
    { key: "item_weight_kg", label: "Weight of one item", unit: "kg" },
    { key: "carton_length_mm", label: "Carton length", unit: "mm" },
    { key: "carton_width_mm", label: "Carton width", unit: "mm" },
    { key: "carton_height_mm", label: "Carton height", unit: "mm" },
    {
      key: "items_per_carton",
      label: "Items per carton",
      unit: "",
      hint: "A full carton may hold at most 10 kg of product.",
    },
    {
      key: "carton_tare_weight_kg",
      label: "Empty carton weight",
      unit: "kg",
      hint: "Optional.",
    },
  ];

function formFrom(profile: ItemPhysicalProfile): FormState {
  const own = profile.entered;
  const text = (value: number | undefined) =>
    value === undefined ? "" : String(value);
  return {
    item_weight_kg: text(own?.item_weight_kg),
    carton_length_mm: text(own?.carton_length_mm),
    carton_width_mm: text(own?.carton_width_mm),
    carton_height_mm: text(own?.carton_height_mm),
    items_per_carton: text(own?.items_per_carton),
    carton_tare_weight_kg: text(own?.carton_tare_weight_kg),
  };
}

function requestFrom(form: FormState): SetPhysicalFactsRequest {
  const number = (value: string) => (value.trim() === "" ? 0 : Number(value));
  return {
    item_weight_kg: number(form.item_weight_kg),
    carton_length_mm: number(form.carton_length_mm),
    carton_width_mm: number(form.carton_width_mm),
    carton_height_mm: number(form.carton_height_mm),
    items_per_carton: Math.trunc(number(form.items_per_carton)),
    carton_tare_weight_kg: number(form.carton_tare_weight_kg),
  };
}

function dimensions(summary: NonNullable<ItemPhysicalProfile["summary"]>) {
  const {
    carton_length_mm: l,
    carton_width_mm: w,
    carton_height_mm: h,
  } = summary;
  return l == null || w == null || h == null ? "—" : `${l} × ${w} × ${h} mm`;
}

export function ItemPhysicalFactsPanel({ itemId }: { itemId: string }) {
  const adapter = useDataAdapter();
  const idBase = useId();
  const [profile, setProfile] = useState<ItemPhysicalProfile | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [form, setForm] = useState<FormState | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldError[]>([]);
  const [otherError, setOtherError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const show = useCallback((next: ItemPhysicalProfile) => {
    setProfile(next);
    setForm(formFrom(next));
  }, []);

  const load = useCallback(() => {
    setLoadFailed(false);
    adapter
      .getCatalogueItemPhysicalProfile(itemId)
      .then(show)
      .catch(() => setLoadFailed(true));
  }, [adapter, itemId, show]);

  useEffect(() => {
    setProfile(null);
    setForm(null);
    load();
  }, [load]);

  const run = async (step: () => Promise<ItemPhysicalProfile>) => {
    setBusy(true);
    setFieldErrors([]);
    setOtherError(null);
    try {
      show(await step());
    } catch (err) {
      const errors = extractFieldErrors(err);
      if (errors.length > 0) setFieldErrors(errors);
      else
        setOtherError(
          mutationErrorMessage(err, "Couldn't save the weight and packing."),
        );
    } finally {
      setBusy(false);
    }
  };

  const messageFor = (field: FormField) =>
    fieldErrors.find((e) => e.field === field)?.message;

  return (
    <section className="space-y-3" aria-label="Weight and packing">
      <div className="flex items-center gap-2">
        <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Weight and packing
        </h3>
        {profile && (
          <Badge
            variant={
              profile.physical_profile_source === "missing"
                ? "warning"
                : "secondary"
            }
          >
            {SOURCE_LABEL[profile.physical_profile_source]}
          </Badge>
        )}
      </div>

      {loadFailed && (
        <p className="text-sm text-destructive">
          Couldn&apos;t load this item&apos;s weight and packing.{" "}
          <button type="button" className="underline" onClick={load}>
            Try again
          </button>
        </p>
      )}
      {!profile && !loadFailed && (
        <p className="text-sm text-muted-foreground">Loading…</p>
      )}

      {profile && form && (
        <>
          {profile.summary && (
            <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1 text-sm">
              <dt className="text-muted-foreground">Weight of one item</dt>
              <dd>{profile.summary.item_weight_kg} kg</dd>
              <dt className="text-muted-foreground">Carton</dt>
              <dd>{dimensions(profile.summary)}</dd>
              <dt className="text-muted-foreground">Items per carton</dt>
              <dd>{profile.summary.items_per_carton ?? "—"}</dd>
            </dl>
          )}
          {profile.physical_profile_source === "missing" && (
            <p className="text-sm text-muted-foreground">
              Nothing gives this item a weight or carton yet. Enter them below
              so stock and despatch can use them.
            </p>
          )}
          {(profile.physical_profile_source === "derived" ||
            profile.physical_profile_source === "supplier") && (
            <p className="text-xs text-muted-foreground">
              These figures come from{" "}
              {profile.physical_profile_source === "derived"
                ? "the product's own definition"
                : "the supplier"}{" "}
              and are not entered here.
            </p>
          )}

          {(profile.physical_profile_source === "missing" ||
            profile.physical_profile_source === "entered") && (
            <form
              className="space-y-3"
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                void run(() =>
                  adapter.setCatalogueItemPhysicalFacts(
                    profile,
                    requestFrom(form),
                  ),
                );
              }}
            >
              {fieldErrors.length > 0 && (
                <p role="alert" className="text-sm text-destructive">
                  {fieldErrors.length}{" "}
                  {fieldErrors.length === 1 ? "problem" : "problems"} to fix
                  before these figures can be saved.
                </p>
              )}
              {otherError && (
                <p role="alert" className="text-sm text-destructive">
                  {otherError}
                </p>
              )}
              <div className="grid gap-3 sm:grid-cols-2">
                {FIELDS.map(({ key, label, unit, hint }) => {
                  const inputId = `${idBase}-${key}`;
                  const message = messageFor(key);
                  return (
                    <div key={key} className="space-y-1">
                      <Label htmlFor={inputId}>
                        {label}
                        {unit && ` (${unit})`}
                      </Label>
                      <Input
                        id={inputId}
                        type="number"
                        inputMode="decimal"
                        min={0}
                        step="any"
                        value={form[key]}
                        aria-invalid={message ? true : undefined}
                        aria-describedby={
                          message ? `${inputId}-error` : undefined
                        }
                        onChange={(e) =>
                          setForm({ ...form, [key]: e.target.value })
                        }
                      />
                      {message ? (
                        <p
                          id={`${inputId}-error`}
                          className="text-sm text-red-600"
                        >
                          {message}
                        </p>
                      ) : (
                        hint && (
                          <p className="text-xs text-muted-foreground">
                            {hint}
                          </p>
                        )
                      )}
                    </div>
                  );
                })}
              </div>
              {profile.entered && (
                <p className="text-xs text-muted-foreground">
                  Entered by {profile.entered.entered_by} on{" "}
                  {formatDateOrDash(profile.entered.entered_at)}.
                </p>
              )}
              <div className="flex gap-2">
                {profile.set_physical_facts_url && (
                  <Button type="submit" size="sm" disabled={busy}>
                    {profile.entered ? "Update figures" : "Save figures"}
                  </Button>
                )}
                {profile.clear_physical_facts_url && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={busy}
                    onClick={() =>
                      void run(() =>
                        adapter.clearCatalogueItemPhysicalFacts(profile),
                      )
                    }
                  >
                    Clear figures
                  </Button>
                )}
              </div>
            </form>
          )}
        </>
      )}
    </section>
  );
}
